import React from 'react';
import { Sparkles, Brain, Zap, Shield, Activity } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const AIEngineSection = () => {
  const { t, isRTL } = useLanguage();

  const capabilities = [
    { icon: Brain, ...t.aiEngine.capabilities[0] },
    { icon: Activity, ...t.aiEngine.capabilities[1] },
    { icon: Zap, ...t.aiEngine.capabilities[2] },
    { icon: Shield, ...t.aiEngine.capabilities[3] }
  ];

  return (
    <section className="py-20 md:py-24 bg-[#1D1D1F] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0071E3]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#5856D6]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-14 ${isRTL ? 'text-center' : ''}`}>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Sparkles className="w-3.5 h-3.5 text-[#0071E3]" />
            <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide font-inter">
              {t.aiEngine.badge}
            </span>
          </div>
          <h2 className="font-dm text-3xl md:text-4xl lg:text-[42px] font-semibold tracking-tight text-white mb-4 leading-tight">
            {t.aiEngine.title}<br />
            <span className="bg-gradient-to-r from-[#0071E3] to-[#5856D6] bg-clip-text text-transparent">
              {t.aiEngine.titleHighlight}
            </span>
          </h2>
          <p className="text-lg text-white/60 font-inter max-w-2xl mx-auto">
            {t.aiEngine.subtitle}
          </p>
        </div>

        {/* AI Visualization */}
        <div className="relative mb-14">
          {/* Central AI Core */}
          <div className="flex justify-center">
            <div className="relative">
              {/* Pulse rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 rounded-full border border-[#0071E3]/20 animate-ping"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 rounded-full border border-[#5856D6]/10"></div>
              </div>
              
              {/* Core */}
              <div className="relative w-24 h-24 bg-gradient-to-br from-[#0071E3] to-[#5856D6] rounded-3xl flex items-center justify-center shadow-lg shadow-[#0071E3]/30">
                <Brain className="w-12 h-12 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {capabilities.map((cap, index) => (
            <div 
              key={index}
              className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 ${isRTL ? 'text-right' : ''}`}
            >
              <div className={`w-12 h-12 bg-gradient-to-br from-[#0071E3]/20 to-[#5856D6]/20 rounded-xl flex items-center justify-center mb-4 ${isRTL ? 'ml-auto' : ''}`}>
                <cap.icon className="w-6 h-6 text-[#0071E3]" />
              </div>
              <h3 className="font-dm text-lg font-semibold text-white mb-2">
                {cap.title}
              </h3>
              <p className="text-white/50 font-inter text-[14px] leading-relaxed">
                {cap.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AIEngineSection;
